import { useState } from 'react';
import { Clock, X, Trash2 } from 'lucide-react';
import { ServiceAddon } from '../../lib/services';
import { supabase } from '../../lib/supabase';

interface AddonLineItemProps {
  addon: ServiceAddon;
  /** booking_services.id this add-on belongs to. */
  bookingServiceId: string;
  canEdit: boolean;
  isPaymentsDisabled: boolean;
  parentLoading: boolean;
  onUpdate: () => Promise<void>;
  onError: (msg: string) => void;
  onRemove: (addonId: string) => void;
}

const money2 = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatDuration(mins: number): string {
  if (mins >= 1440) return `${Math.floor(mins / 1440)} days`;
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} hr ${m} min` : `${h} hours`;
  }
  return `${mins} minutes`;
}

/** One add-on rendered as an independent line item: its own discount, price edit, and remove. */
export default function AddonLineItem({
  addon,
  bookingServiceId,
  canEdit,
  isPaymentsDisabled,
  parentLoading,
  onUpdate,
  onError,
  onRemove,
}: AddonLineItemProps) {
  const [busy, setBusy] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [dType, setDType] = useState<'fixed' | 'percentage'>(addon.discount_type === 'percentage' ? 'percentage' : 'fixed');
  const [dVal, setDVal] = useState<string>(Number(addon.discount_value ?? 0) > 0 ? String(addon.discount_value) : '');
  const [showEdit, setShowEdit] = useState(false);
  const [editPrice, setEditPrice] = useState<string>(String(addon.price ?? 0));

  const price = addon.price || 0;
  const dvNum = Number(addon.discount_value ?? 0);
  const discountAmount = Math.min(
    addon.discount_type === 'percentage' ? (price * dvNum) / 100 : dvNum,
    price,
  );
  const hasDiscount = dvNum > 0 && discountAmount > 0;
  const net = Math.max(Math.round((price - discountAmount) * 100) / 100, 0);

  const updateRow = async (patch: Record<string, any>) => {
    if (!supabase) { onError('Supabase client is not initialized'); return false; }
    const { error } = await supabase
      .from('booking_service_addons')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('booking_service_id', bookingServiceId)
      .eq('addon_id', addon.id);
    if (error) { onError(error.message); return false; }
    return true;
  };

  const handleSaveDiscount = async () => {
    setBusy(true);
    try {
      const val = Math.max(parseFloat(dVal) || 0, 0);
      if (await updateRow({ discount_type: dType, discount_value: val })) {
        await onUpdate();
        setShowDiscount(false);
      }
    } finally { setBusy(false); }
  };

  const handleClearDiscount = async () => {
    setBusy(true);
    try {
      if (await updateRow({ discount_type: 'fixed', discount_value: 0 })) {
        setDVal('');
        setDType('fixed');
        await onUpdate();
        setShowDiscount(false);
      }
    } finally { setBusy(false); }
  };

  const handleSavePrice = async () => {
    setBusy(true);
    try {
      const p = Math.max(parseFloat(editPrice) || 0, 0);
      if (await updateRow({ price: p })) {
        await onUpdate();
        setShowEdit(false);
      }
    } finally { setBusy(false); }
  };

  const disabled = busy || parentLoading;

  return (
    <div className="bg-gray-50/50 rounded-[8px] px-2 py-1.5">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <span className="text-[12px] font-medium text-gray-900">{addon.name}</span>
          <div className="flex items-center mt-0.5 text-[10px] text-gray-500">
            <Clock className="h-2.5 w-2.5 mr-0.5 flex-shrink-0" />
            {formatDuration(addon.duration_minutes)}
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-2">
          {!isPaymentsDisabled && (
            hasDiscount ? (
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] font-semibold text-gray-900">${money2(net)}</span>
                <span className="text-[11px] text-gray-400 line-through">${money2(price)}</span>
                {canEdit && (
                  <button
                    onClick={handleClearDiscount}
                    disabled={disabled}
                    className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                    title="Remove discount"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            ) : (
              <span className="text-[12px] font-semibold text-gray-900">${money2(price)}</span>
            )
          )}
          {canEdit && (
            <button
              onClick={() => onRemove(addon.id)}
              disabled={disabled}
              className="h-6 w-6 flex items-center justify-center bg-red-50 text-red-600 rounded-full hover:bg-red-100 active:bg-red-200 active:scale-95 transition-all duration-150 touch-manipulation disabled:opacity-50"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {canEdit && (
        <div className="flex items-center gap-2 mt-1.5">
          <button
            onClick={() => { setShowEdit((v) => !v); setShowDiscount(false); }}
            disabled={disabled}
            className="px-2 py-1 bg-blue-50 text-blue-600 rounded-[7px] text-[10px] font-medium hover:bg-blue-100 active:scale-95 transition-all disabled:opacity-50"
          >
            Edit
          </button>
          <button
            onClick={() => { setShowDiscount((v) => !v); setShowEdit(false); }}
            disabled={disabled}
            className={`px-2 py-1 rounded-[7px] text-[10px] font-medium active:scale-95 transition-all disabled:opacity-50 ${
              hasDiscount ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Discount
          </button>
        </div>
      )}

      {canEdit && showEdit && (
        <div className="mt-1.5 flex items-center gap-2">
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">$</span>
            <input
              type="number"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              className="w-24 pl-5 pr-2 py-1 bg-white border border-gray-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <button onClick={handleSavePrice} disabled={disabled} className="px-2.5 py-1 text-[11px] font-semibold text-white bg-blue-600 rounded-lg disabled:opacity-50">Save</button>
          <button onClick={() => { setShowEdit(false); setEditPrice(String(addon.price ?? 0)); }} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      {canEdit && showDiscount && (
        <div className="mt-1.5 flex items-center gap-2">
          <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden flex-shrink-0">
            <button type="button" onClick={() => setDType('fixed')} className={`px-2 py-1 text-[11px] font-semibold ${dType === 'fixed' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>$</button>
            <button type="button" onClick={() => setDType('percentage')} className={`px-2 py-1 text-[11px] font-semibold ${dType === 'percentage' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}>%</button>
          </div>
          <input
            type="number"
            value={dVal}
            onChange={(e) => setDVal(e.target.value)}
            placeholder={dType === 'percentage' ? '0%' : '0.00'}
            className="w-20 px-2 py-1 bg-white border border-gray-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
          <button onClick={handleSaveDiscount} disabled={disabled || !dVal} className="px-2.5 py-1 text-[11px] font-semibold text-white bg-blue-600 rounded-lg disabled:opacity-50">Save</button>
          {hasDiscount && (
            <button onClick={handleClearDiscount} disabled={disabled} className="px-2.5 py-1 text-[11px] font-semibold text-red-600 bg-red-50 rounded-lg disabled:opacity-50">Remove</button>
          )}
        </div>
      )}
    </div>
  );
}
